import React, { useState } from 'react';
import Navbar from './Navbar';
import WebMusicPlayer from './WebMusicPlayer';
import './PlayPage.css';

interface AlbumContent {
  title: string;
  artist: string;
  artwork: string;
  tracks: Array<{
    id: number;
    title: string;
    duration: string;
    audioUrl: string;
  }>;
  media?: Array<{
    type: 'text' | 'image';
    content: string;
    caption?: string;
  }>;
  artistBio?: string;
  artistPhoto?: string;
  artistInstagram?: string;
  artistWebsite?: string;
}

function PlayPage() {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [albumContent, setAlbumContent] = useState<AlbumContent | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const submitCode = async (codeToSubmit: string) => {
    const cleanCode = codeToSubmit.toUpperCase().replace(/\s/g, '');
    if (cleanCode.length !== 5) {
      setError('Please enter a 5-character code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/play/${cleanCode}`);
      const data = await response.json();

      if (data.success) {
        setAlbumContent(data.content);
        setExpiresAt(data.expiresAt);
      } else {
        setError(data.error || 'Invalid or expired code');
      }
    } catch (err) {
      setError('Failed to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitCode(code);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 5) {
      setCode(value);
      setError(null);

      // Auto-submit when 5 characters are entered (with delay for animation)
      if (value.length === 5) {
        setIsAutoSubmitting(true);
        setTimeout(() => {
          submitCode(value);
          setIsAutoSubmitting(false);
        }, 500);
      }
    }
  };

  if (albumContent) {
    return (
      <WebMusicPlayer
        content={albumContent}
        expiresAt={expiresAt}
        onBack={() => {
          setAlbumContent(null);
          setCode('');
        }}
      />
    );
  }

  return (
    <div className="play-page">
      <Navbar />

      <div className="play-content">
        <div className="play-container">
          <div className="play-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </div>

          <h1 className="play-title">Enter Your Code</h1>
          <p className="play-subtitle">
            Type the 5-character code from your phone
          </p>

          <form onSubmit={handleSubmit} className="play-form">
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="A7K2P"
              className="play-input"
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
            />

            {error && <p className="play-error">{error}</p>}

            <button
              type="submit"
              className={`play-button ${isAutoSubmitting ? 'auto-submitting' : ''}`}
              disabled={isLoading || code.length !== 5}
            >
              <span className="button-fill"></span>
              <span className="button-text">{isLoading ? 'Loading...' : 'Play Album'}</span>
            </button>
          </form>

          <p className="play-help">
            Scan an album card with your phone, then tap "Play on Desktop" to get a code
          </p>
        </div>
      </div>
    </div>
  );
}

export default PlayPage;
